import {
  ApartmentCategoryEnum,
  BuildingTypeEnum,
  HouseCategoryEnum,
  LandCategoryEnum,
  OfficeCategoryEnum,
  OfficeTypeEnum,
  PurposeEnum,
  Search,
  SortingEnum,
  TypeEnum,
} from "@/types/types";
import { sqftToM2 } from "../sqftToM2";

const KEYS_ALLOW_NEGATIVE_ONE = new Set(["max_buyers_fee"]);

const KEYS_ALLOW_ZERO = new Set([
  "top_left_lat",
  "top_left_lon",
  "bottom_right_lat",
  "bottom_right_lon",
  "center_lat",
  "center_lon",
  "zoom",
  // add "page_number" here if you ever allow 0 as a valid page
]);

function normalizeQueryParams(
  params: Record<string, any>,
  {
    allowZeroKeys = KEYS_ALLOW_ZERO,
    includeNegativeOneKeys = new Set<string>(),
  }: { allowZeroKeys?: Set<string>; includeNegativeOneKeys?: Set<string> } = {},
): Record<string, string> {
  const out: Record<string, string> = {};

  for (const [key, val] of Object.entries(params)) {
    if (val === null || val === undefined) continue;

    switch (typeof val) {
      case "boolean":
        // only include true booleans; drop false
        if (val) out[key] = "true";
        break;
      case "number":
        if (!Number.isFinite(val)) break;
        // keep 0 only for selected keys
        if (val === 0 && !allowZeroKeys.has(key)) break;
        if (val === -1 && !includeNegativeOneKeys.has(key)) break;
        out[key] = String(val);
        break;
      case "string":
        if (val.trim().length > 0) out[key] = val;
        break;
      default:
        // arrays/objects: stringify if you ever pass any (you shouldn't for URL qs)
        // out[key] = JSON.stringify(val);
        break;
    }
  }

  return out;
}

export function buildMapParams(
  search: Search,
  locale: string,
  userSub: string | null,
  isImperial: boolean,
  rate: number,
): Record<string, string> {
  const params: Record<string, any> = {
    location_url: search.location_url,
    type: search.type,
    purpose: search.purpose,
    locale,
    user_sub: userSub,
    top_left_lat: search.bouding_box.top_left_lat,
    top_left_lon: search.bouding_box.top_left_lon,
    bottom_right_lat: search.bouding_box.bottom_right_lat,
    bottom_right_lon: search.bouding_box.bottom_right_lon,
    zoom: search.bouding_box.zoom,

    category: search.category,

    number_of_bathrooms: search.bathrooms.number,
    fixed_number_of_bathrooms: search.bathrooms.is_fixed,
    number_of_rooms: search.rooms.number,
    fixed_number_of_rooms: search.rooms.is_fixed,
    number_of_bedrooms: search.bedrooms.number,
    fixed_number_of_bedrooms: search.bedrooms.is_fixed,
    min_price: search.price.min * rate,
    max_price: search.price.max * rate,
    min_interior_space: isImperial
      ? sqftToM2(search.interior_space.min)
      : search.interior_space.min,
    max_interior_space: isImperial
      ? sqftToM2(search.interior_space.max)
      : search.interior_space.max,
    min_lot_size: isImperial
      ? sqftToM2(search.lot_size.min)
      : search.lot_size.min,
    max_lot_size: isImperial
      ? sqftToM2(search.lot_size.max)
      : search.lot_size.max,
    min_year_built: search.year_built.min,
    max_year_built: search.year_built.max,
    max_buyers_fee: search.maxBuyersFee,
    building_type: search.building_type,
    new_development: search.new_development,
    office_type: search.office_type,
    has_pool: search.has_pool,
    has_parking: search.has_parking,
    has_fitness_center: search.has_fitness_center,
    has_elevator: search.has_elevator,
    has_concierge: search.has_concierge,
    has_security: search.has_security,
    has_public_roof_deck: search.has_public_roof_deck,
    has_roof_deck: search.has_roof_deck,
    has_garage: search.has_garage,
    has_waterfront: search.has_waterfront,
    has_balcony: search.has_balcony,
    has_furniture: search.has_furniture,
    has_garden: search.has_garden,
    has_wine_cellar: search.has_wine_cellar,
    has_ac: search.has_ac,
    has_sauna: search.has_sauna,
    has_gated_community: search.has_gated_community,
    is_pet_friendly: search.is_pet_friendly,
    has_utilities_included: search.has_utilities_included,
    has_disabled_access: search.has_disabled_access,
    is_ready_to_move_in: search.is_ready_to_move_in,
    has_gas: search.has_gas,
    has_electricity: search.has_electricity,
    has_water: search.has_water,
    has_sewage: search.has_sewage,
    has_cafeteria: search.has_cafeteria,
    has_conference_room: search.has_conference_room,
    has_public_transport: search.has_public_transport,
    has_hoa: search.has_hoa,
    sea_view: search.sea_view,
    mountain_view: search.mountain_view,
    countryside_view: search.countryside_view,
    city_view: search.city_view,
    north: search.north,
    south: search.south,
    east: search.east,
    west: search.west,
    is_luxury: search.is_luxury,
    draw_geojson: search.draw_geojson,
    target_agency_id: search.target_agency_id,
    target_user_id: search.target_user_id,
  };

  // Filter out null/undefined/false to avoid bloated URLs
  return normalizeQueryParams(params, {
    includeNegativeOneKeys: new Set(["max_buyers_fee"]),
  });
}

export function buildSearchParams(
  search: Search,
  locale: string,
  userSub: string | null,
  isImperial: boolean,
  rate: number,
): Record<string, string> {
  const params: Record<string, any> = {
    location_url: search.location_url,
    type: search.type,
    purpose: search.purpose,
    page_number: search.page_number,
    locale,
    user_sub: userSub,
    top_listings_sort: search.sorting === SortingEnum.TOPLISTINGS,
    lowest_price_sort: search.sorting === SortingEnum.LOWESTPRICE,
    highest_price_sort: search.sorting === SortingEnum.HIGHESTPRICE,
    newest_listings_sort: search.sorting === SortingEnum.NEWESTLISTINGS,
    top_left_lat: search.bouding_box.top_left_lat,
    top_left_lon: search.bouding_box.top_left_lon,
    bottom_right_lat: search.bouding_box.bottom_right_lat,
    bottom_right_lon: search.bouding_box.bottom_right_lon,
    zoom: search.bouding_box.zoom,

    center_lat: search.center.lat,
    center_lon: search.center.lon,

    category: search.category,

    number_of_bathrooms: search.bathrooms.number,
    fixed_number_of_bathrooms: search.bathrooms.is_fixed,
    number_of_rooms: search.rooms.number,
    fixed_number_of_rooms: search.rooms.is_fixed,
    number_of_bedrooms: search.bedrooms.number,
    fixed_number_of_bedrooms: search.bedrooms.is_fixed,
    min_price: search.price.min * rate,
    max_price: search.price.max * rate,
    min_interior_space: isImperial
      ? sqftToM2(search.interior_space.min)
      : search.interior_space.min,
    max_interior_space: isImperial
      ? sqftToM2(search.interior_space.max)
      : search.interior_space.max,
    min_lot_size: isImperial
      ? sqftToM2(search.lot_size.min)
      : search.lot_size.min,
    max_lot_size: isImperial
      ? sqftToM2(search.lot_size.max)
      : search.lot_size.max,
    min_year_built: search.year_built.min,
    max_year_built: search.year_built.max,
    max_buyers_fee: search.maxBuyersFee,
    building_type: search.building_type,
    new_development: search.new_development,
    office_type: search.office_type,
    has_pool: search.has_pool,
    has_parking: search.has_parking,
    has_fitness_center: search.has_fitness_center,
    has_elevator: search.has_elevator,
    has_concierge: search.has_concierge,
    has_security: search.has_security,
    has_public_roof_deck: search.has_public_roof_deck,
    has_roof_deck: search.has_roof_deck,
    has_garage: search.has_garage,
    has_waterfront: search.has_waterfront,
    has_balcony: search.has_balcony,
    has_furniture: search.has_furniture,
    has_garden: search.has_garden,
    has_wine_cellar: search.has_wine_cellar,
    has_ac: search.has_ac,
    has_sauna: search.has_sauna,
    has_gated_community: search.has_gated_community,
    is_pet_friendly: search.is_pet_friendly,
    has_utilities_included: search.has_utilities_included,
    has_disabled_access: search.has_disabled_access,
    is_ready_to_move_in: search.is_ready_to_move_in,
    has_gas: search.has_gas,
    has_electricity: search.has_electricity,
    has_water: search.has_water,
    has_sewage: search.has_sewage,
    has_cafeteria: search.has_cafeteria,
    has_conference_room: search.has_conference_room,
    has_public_transport: search.has_public_transport,
    has_hoa: search.has_hoa,
    sea_view: search.sea_view,
    mountain_view: search.mountain_view,
    countryside_view: search.countryside_view,
    city_view: search.city_view,
    north: search.north,
    south: search.south,
    east: search.east,
    west: search.west,
    is_luxury: search.is_luxury,
    location_name: search.location_name,
    listing_count: search.listing_count,
    draw_geojson: search.draw_geojson,
    target_agency_id: search.target_agency_id,
    target_user_id: search.target_user_id,
  };

  // Filter out null/undefined/false to avoid bloated URLs
  return normalizeQueryParams(params, {
    includeNegativeOneKeys: new Set(["max_buyers_fee"]),
  });
}

// will build the params for the new url update
export function buildSearchParamsForUrlUpdate(
  search: Search,
  locale: string,
  isImperial: boolean,
  rate: number,
): Record<string, string> {
  const params: Record<string, any> = {
    sorting: search.sorting,
    top_left_lat: search.bouding_box.top_left_lat,
    top_left_lon: search.bouding_box.top_left_lon,
    bottom_right_lat: search.bouding_box.bottom_right_lat,
    bottom_right_lon: search.bouding_box.bottom_right_lon,
    zoom: search.bouding_box.zoom,

    center_lat: search.center.lat,
    center_lon: search.center.lon,

    category: search.category,

    number_of_bathrooms: search.bathrooms.number,
    fixed_number_of_bathrooms: search.bathrooms.is_fixed,
    number_of_rooms: search.rooms.number,
    fixed_number_of_rooms: search.rooms.is_fixed,
    number_of_bedrooms: search.bedrooms.number,
    fixed_number_of_bedrooms: search.bedrooms.is_fixed,
    min_price: search.price.min * rate,
    max_price: search.price.max * rate,
    min_interior_space: isImperial
      ? sqftToM2(search.interior_space.min)
      : search.interior_space.min,
    max_interior_space: isImperial
      ? sqftToM2(search.interior_space.max)
      : search.interior_space.max,
    min_lot_size: isImperial
      ? sqftToM2(search.lot_size.min)
      : search.lot_size.min,
    max_lot_size: isImperial
      ? sqftToM2(search.lot_size.max)
      : search.lot_size.max,
    min_year_built: search.year_built.min,
    max_year_built: search.year_built.max,
    max_buyers_fee: search.maxBuyersFee,
    building_type: search.building_type,
    new_development: search.new_development,
    office_type: search.office_type,
    has_pool: search.has_pool,
    has_parking: search.has_parking,
    has_fitness_center: search.has_fitness_center,
    has_elevator: search.has_elevator,
    has_concierge: search.has_concierge,
    has_security: search.has_security,
    has_public_roof_deck: search.has_public_roof_deck,
    has_roof_deck: search.has_roof_deck,
    has_garage: search.has_garage,
    has_waterfront: search.has_waterfront,
    has_balcony: search.has_balcony,
    has_furniture: search.has_furniture,
    has_garden: search.has_garden,
    has_wine_cellar: search.has_wine_cellar,
    has_ac: search.has_ac,
    has_sauna: search.has_sauna,
    has_gated_community: search.has_gated_community,
    is_pet_friendly: search.is_pet_friendly,
    has_utilities_included: search.has_utilities_included,
    has_disabled_access: search.has_disabled_access,
    is_ready_to_move_in: search.is_ready_to_move_in,
    has_gas: search.has_gas,
    has_electricity: search.has_electricity,
    has_water: search.has_water,
    has_sewage: search.has_sewage,
    has_cafeteria: search.has_cafeteria,
    has_conference_room: search.has_conference_room,
    has_public_transport: search.has_public_transport,
    has_hoa: search.has_hoa,
    sea_view: search.sea_view,
    mountain_view: search.mountain_view,
    countryside_view: search.countryside_view,
    city_view: search.city_view,
    north: search.north,
    south: search.south,
    east: search.east,
    west: search.west,
    is_luxury: search.is_luxury,
    location_name: search.location_name,
    listing_count: search.listing_count,
    draw_geojson: search.draw_geojson,
    target_agency_id: search.target_agency_id,
    target_user_id: search.target_user_id,
  };

  // Filter out null/undefined/false to avoid bloated URLs
  return normalizeQueryParams(params);
}

export function buildSearchParamsAsDeafult(
  search: Search,
  locale: string,
  userSub: string | null,
  locationName: string,
  listingCount: string,
): Record<string, string> {
  const params: Record<string, any> = {
    locale,
    user_sub: userSub,
    top_listings_sort: search.sorting === SortingEnum.TOPLISTINGS,
    lowest_price_sort: search.sorting === SortingEnum.LOWESTPRICE,
    highest_price_sort: search.sorting === SortingEnum.HIGHESTPRICE,
    newest_listings_sort: search.sorting === SortingEnum.NEWESTLISTINGS,
    top_left_lat: search.bouding_box.top_left_lat,
    top_left_lon: search.bouding_box.top_left_lon,
    bottom_right_lat: search.bouding_box.bottom_right_lat,
    bottom_right_lon: search.bouding_box.bottom_right_lon,
    zoom: search.bouding_box.zoom,

    center_lat: search.center.lat,
    center_lon: search.center.lon,

    number_of_bathrooms: search.bathrooms.number,
    fixed_number_of_bathrooms: search.bathrooms.is_fixed,
    number_of_rooms: search.rooms.number,
    fixed_number_of_rooms: search.rooms.is_fixed,
    number_of_bedrooms: search.bedrooms.number,
    fixed_number_of_bedrooms: search.bedrooms.is_fixed,
    min_price: search.price.min,
    max_price: search.price.max,
    min_interior_space: search.interior_space.min,
    max_interior_space: search.interior_space.max,
    min_lot_size: search.lot_size.min,
    max_lot_size: search.lot_size.max,
    min_year_built: search.year_built.min,
    max_year_built: search.year_built.max,
    max_buyers_fee: search.maxBuyersFee,
    is_luxury: search.is_luxury,
    new_development: search.new_development,
    location_name: locationName,
    listing_count: listingCount,
    target_agency_id: search.target_agency_id,
    target_user_id: search.target_user_id,
  };

  // Filter out null/undefined/false to avoid bloated URLs
  return normalizeQueryParams(params);
}

export function buildLocationSearch(
  search: Partial<Search>,
  locationName: string,
  listingCount: string | number,
): Record<string, any> {
  const params: Record<string, any> = {
    sorting: SortingEnum.TOPLISTINGS,

    top_left_lat: search.bouding_box?.top_left_lat ?? 0,
    top_left_lon: search.bouding_box?.top_left_lon ?? 0,
    bottom_right_lat: search.bouding_box?.bottom_right_lat ?? 0,
    bottom_right_lon: search.bouding_box?.bottom_right_lon ?? 0,
    zoom: search.bouding_box?.zoom ?? 0,

    center_lat: search.center?.lat ?? 0,
    center_lon: search.center?.lon ?? 0,
    location_name: locationName,
    listing_count: toStringSafe(listingCount),
  };
  return normalizeQueryParams(params);
}

export function mapSearchParamsToSearch(
  searchParams: Record<string, string | string[] | undefined>,
  routeParams: {
    location_url: string;
    type: TypeEnum;
    purpose: PurposeEnum;
    page_number: string;
  },
): Search {
  return {
    location_url: routeParams.location_url,
    type: routeParams.type,
    purpose: routeParams.purpose,
    sorting: toEnum(searchParams.sorting, SortingEnum, SortingEnum.TOPLISTINGS),
    page_number: toNumber(routeParams.page_number, 1),

    bouding_box: {
      top_left_lat: toNumber(searchParams.top_left_lat),
      top_left_lon: toNumber(searchParams.top_left_lon),
      bottom_right_lat: toNumber(searchParams.bottom_right_lat),
      bottom_right_lon: toNumber(searchParams.bottom_right_lon),
      zoom: toNumber(searchParams.zoom),
    },

    center: {
      lat: toNumber(searchParams.center_lat),
      lon: toNumber(searchParams.center_lon),
    },

    rooms: {
      number: toNumber(searchParams.number_of_rooms),
      is_fixed: toBool(searchParams.fixed_number_of_rooms),
    },
    bedrooms: {
      number: toNumber(searchParams.number_of_bedrooms),
      is_fixed: toBool(searchParams.fixed_number_of_bedrooms),
    },
    bathrooms: {
      number: toNumber(searchParams.number_of_bathrooms),
      is_fixed: toBool(searchParams.fixed_number_of_bathrooms),
    },

    price: {
      min: toNumber(searchParams.min_price),
      max: toNumber(searchParams.max_price),
    },
    interior_space: {
      min: toNumber(searchParams.min_interior_space),
      max: toNumber(searchParams.max_interior_space),
    },
    lot_size: {
      min: toNumber(searchParams.min_lot_size),
      max: toNumber(searchParams.max_lot_size),
    },
    year_built: {
      min: toNumber(searchParams.min_year_built),
      max: toNumber(searchParams.max_year_built),
    },

    maxBuyersFee: toNumber(searchParams.max_buyers_fee, -1),
    is_luxury: toBool(searchParams.is_luxury),

    category: searchParams.category as
      | ApartmentCategoryEnum
      | HouseCategoryEnum
      | LandCategoryEnum
      | OfficeCategoryEnum
      | null,

    building_type: (searchParams.building_type as BuildingTypeEnum) ?? null,
    new_development: toBool(searchParams.new_development),
    office_type: (searchParams.office_type as OfficeTypeEnum) ?? null,

    has_parking: toBool(searchParams.has_parking),
    has_pool: toBool(searchParams.has_pool),
    has_fitness_center: toBool(searchParams.has_fitness_center),
    has_elevator: toBool(searchParams.has_elevator),
    has_concierge: toBool(searchParams.has_concierge),
    has_security: toBool(searchParams.has_security),
    has_public_roof_deck: toBool(searchParams.has_public_roof_deck),
    has_roof_deck: toBool(searchParams.has_roof_deck),
    has_garage: toBool(searchParams.has_garage),
    has_waterfront: toBool(searchParams.has_waterfront),
    has_balcony: toBool(searchParams.has_balcony),
    has_furniture: toBool(searchParams.has_furniture),
    has_garden: toBool(searchParams.has_garden),
    has_wine_cellar: toBool(searchParams.has_wine_cellar),
    has_ac: toBool(searchParams.has_ac),
    has_sauna: toBool(searchParams.has_sauna),
    has_gated_community: toBool(searchParams.has_gated_community),
    is_pet_friendly: toBool(searchParams.is_pet_friendly),
    has_utilities_included: toBool(searchParams.has_utilities_included),
    has_disabled_access: toBool(searchParams.has_disabled_access),
    is_ready_to_move_in: toBool(searchParams.is_ready_to_move_in),
    has_gas: toBool(searchParams.has_gas),
    has_electricity: toBool(searchParams.has_electricity),
    has_water: toBool(searchParams.has_water),
    has_sewage: toBool(searchParams.has_sewage),
    has_cafeteria: toBool(searchParams.has_cafeteria),
    has_conference_room: toBool(searchParams.has_conference_room),
    has_public_transport: toBool(searchParams.has_public_transport),
    has_hoa: toBool(searchParams.has_hoa),

    sea_view: toBool(searchParams.sea_view),
    mountain_view: toBool(searchParams.mountain_view),
    countryside_view: toBool(searchParams.countryside_view),
    city_view: toBool(searchParams.city_view),

    north: toBool(searchParams.north),
    south: toBool(searchParams.south),
    east: toBool(searchParams.east),
    west: toBool(searchParams.west),
    location_name: toStringValue(searchParams.location_name),
    listing_count: toNumber(searchParams.listing_count),
    draw_geojson: toStringValue(searchParams.draw_geojson),
    target_agency_id: toStringValue(searchParams.target_agency_id),
    target_user_id: toStringValue(searchParams.target_user_id),
  };
}

function toNumber(value: string | string[] | undefined, fallback = 0): number {
  if (!value) return fallback;
  const num = Array.isArray(value) ? parseFloat(value[0]) : parseFloat(value);
  return isNaN(num) ? fallback : num;
}

function toStringValue(
  value: string | string[] | undefined,
  fallback = "",
): string {
  if (!value) return fallback;
  const str = Array.isArray(value) ? value[0] : value;
  return typeof str === "string" && str.length > 0 ? str : fallback;
}

export function toStringSafe(
  value: string | number | undefined | null,
  fallback = "",
): string {
  if (value === undefined || value === null) return fallback;
  return typeof value === "number" ? String(value) : value;
}

function toBool(
  value: string | string[] | undefined,
  fallback = false,
): boolean {
  if (!value) return fallback;
  const v = Array.isArray(value) ? value[0] : value;
  return v === "true" || v === "1";
}

function toEnum<T extends Record<string, string>>(
  value: string | string[] | undefined,
  enumObj: T,
  fallback: T[keyof T],
): T[keyof T] {
  if (!value) return fallback;
  const v = Array.isArray(value) ? value[0] : value;
  const values = Object.values(enumObj) as string[];
  return values.includes(v) ? (v as T[keyof T]) : fallback;
}

export const isTypeEnum = (value: any): value is TypeEnum =>
  Object.values(TypeEnum).includes(value as TypeEnum);

export const isPurposeEnum = (value: any): value is PurposeEnum =>
  Object.values(PurposeEnum).includes(value as PurposeEnum);
